import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeToExpiredLinkModal,
  closeExpiredLinkModal,
  setExpiredLinkModalStatus,
} from '../../services/expiredLinkModalService';
import { resendSecretLink } from '../../services/authService';
import { logError } from '../../services/loggerService';
import { isApiError } from '../../lib/api/errors';
import { Modal } from '../feedback/Modal';
import { Button } from '../actions/Button';

/**
 * Opens when /login-secret returns 401 for a `?s=<secret>` link (an alert
 * email or SMS whose link has expired). The BE re-sends on the channel the
 * original notification used, so there is nothing to ask the user.
 */
export function ExpiredLinkModal() {
  const { t } = useTranslation('common');
  const [state, setState] = useState(() => ({
    isOpen: false,
    status: 'idle' as 'idle' | 'sending' | 'sent' | 'error',
    errorMessage: undefined as string | undefined,
    expiredSecret: undefined as string | undefined,
  }));

  useEffect(() => subscribeToExpiredLinkModal(setState), []);

  const resend = async () => {
    if (!state.expiredSecret) return;
    setExpiredLinkModalStatus('sending');
    try {
      await resendSecretLink(state.expiredSecret);
      setExpiredLinkModalStatus('sent');
    } catch (err) {
      logError(err instanceof Error ? err : new Error(String(err)), 'Failed to resend secret link');
      // 403 = not renewable, 404 = no notification to resend: sign in normally instead.
      const notRenewable = isApiError(err) && (err.status === 403 || err.status === 404);
      setExpiredLinkModalStatus(
        'error',
        t(notRenewable ? 'ui.expiredLink.errorNotRenewable' : 'ui.expiredLink.errorGeneric'),
      );
    }
  };

  const { status } = state;
  return (
    <Modal
      isOpen={state.isOpen}
      onClose={closeExpiredLinkModal}
      size="md"
      title={status === 'sent' ? t('ui.expiredLink.titleSent') : t('ui.expiredLink.titleExpired')}
      footer={
        status === 'sending' ? undefined : status === 'sent' ? (
          <Button size="sm" onClick={closeExpiredLinkModal}>
            {t('actions.close')}
          </Button>
        ) : (
          <>
            <Button variant="ghost-dark" size="sm" onClick={closeExpiredLinkModal}>
              {status === 'error' ? t('actions.close') : t('actions.cancel')}
            </Button>
            <Button size="sm" onClick={resend}>
              {status === 'error' ? t('actions.retry') : t('ui.expiredLink.resendCta')}
            </Button>
          </>
        )
      }
    >
      <p className="m-0 text-sm text-muted">
        {status === 'idle' && t('ui.expiredLink.bodyExpired')}
        {status === 'sending' && t('ui.expiredLink.sending')}
        {status === 'sent' && t('ui.expiredLink.bodySent')}
        {status === 'error' && state.errorMessage}
      </p>
    </Modal>
  );
}
